import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';

import { CustomerProfile, Promotion, RecentOrFavoriteOrder, PreConfiguredMeal, PromoBanner, LargeOrderPromo, LargeOrderPromoCard, OrderType, MealType } from '@/models';
import { ui } from '@/constants';
import { CountryCode } from '@/store/ui';
import { GETTERS as CUSTOMER_GETTERS } from '@/store/customer';
import { isLoyaltyEnabled } from '@/helpers/country';
import { ExternalCampaignEvent, HomePageLoadEvent } from '@/services/analytics/events';
import { AEMHomepageXfs, getAemXfHtml } from '@/models/aem';

@Component({
  name: 'CmgAemHome'
})
export default class CmgAemHome extends Vue {
  watchers: (() => void)[] = [];
  private hasScrolledToHash: boolean = false;
  private isLoadingFavorites: boolean = false;
  private isLoadingAemXfs: boolean = false;
  private isAemHeroXFMissing: boolean = false;

  // #region Common
  get isDesktop(): boolean {
    return this.$appStore.state.ui.clientWidth > ui.breakpoints.tabletMax;
  }

  get isLoggedIn(): boolean {
    return this.$services.customer.isLoggedIn() === true;
  }

  get isOrderOnline(): boolean {
    if(this.$appStore.state.featureFlags.orderingStatus?.isOnline !== undefined ){
      return this.$appStore.state.featureFlags.orderingStatus.isOnline;
    }
    return true;
	}

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }
  // #endregion

  // #region Recents & Favorites
  get recentOrders(): RecentOrFavoriteOrder[] {
    return this.$appStore.getters[CUSTOMER_GETTERS.RECENT_ORDERS];
  }

  get favoriteOrders(): RecentOrFavoriteOrder[] {
    return this.$appStore.getters[CUSTOMER_GETTERS.FAVORITE_ORDERS];
  }
  // #endregion

  // #region Promo Banners
  get promoBannerTopGuestDesktop(): PromoBanner | null {
    if (this.countryCode == CountryCode.CA) {
      return this.$appStore.state.cms.promoBannerTopGuestDesktopCanada;
    }
    return this.$appStore.state.cms.promoBannerTopGuestDesktop;
  }

  get promoBannerTopGuestMobile(): PromoBanner | null {
    if (this.countryCode == CountryCode.CA) {
      return this.$appStore.state.cms.promoBannerTopGuestMobileCanada;
    }
    return this.$appStore.state.cms.promoBannerTopGuestMobile;
  }

  get promoBannerTopAuthenticatedDesktop(): PromoBanner | null {
    if (this.countryCode == CountryCode.CA) {
      return this.$appStore.state.cms.promoBannerTopAuthenticatedDesktopCanada;
    }
    return this.$appStore.state.cms.promoBannerTopAuthenticatedDesktop;
  }

  get promoBannerTopAuthenticatedMobile(): PromoBanner | null {
    if (this.countryCode == CountryCode.CA) {
      return this.$appStore.state.cms.promoBannerTopAuthenticatedMobileCanada;
    }
    return this.$appStore.state.cms.promoBannerTopAuthenticatedMobile;
  }

  get promoBannerTop(): PromoBanner | null {
    if (this.isLoggedIn) {
      if (this.isDesktop) {
        return this.promoBannerTopAuthenticatedDesktop;
      } else {
        return this.promoBannerTopAuthenticatedMobile;
      }
    } else {
      if (this.isDesktop) {
        return this.promoBannerTopGuestDesktop;
      } else {
        return this.promoBannerTopGuestMobile;
      }
    }
  }

  get promoBannerBottom(): PromoBanner | null {
    if (this.countryCode == CountryCode.CA) {
      return this.$appStore.state.cms.promoBannerBottomCanada;
    }
    return this.$appStore.state.cms.promoBannerBottom;
  }

  // #endregion

  // #region Featured
  get featuredData(): PreConfiguredMeal[] {
    return this.$appStore.state.menu.preConfiguredMeals.filter(meal => meal.mealType == MealType.Influencer);
  }

  // #endregion

  // #region Loyalty Pepper
  get promotions(): Promotion[] {
    return this.$appStore.state.customer.wallet.promotions;
  }

  get countryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get hasPromotions(): boolean {
    return this.$appStore.state.customer.wallet.promotions.length > 0;
  }
  // #endregion

  // #region Group Order / Catering Promo
  get largeOrderPromo(): LargeOrderPromo | null {
    return this.$appStore.state.cms.largeOrderPromo;
  }

  get groupOrderPromo(): LargeOrderPromoCard | null {
    return this.$appStore.state.cms.groupOrderCard;
  }

  get cateringPromo(): LargeOrderPromoCard | null {
    if (this.countryCode == CountryCode.CA) {
      return this.$appStore.state.cms.cateringCardCanada;
    }
    return this.$appStore.state.cms.cateringCard;
  }
  // #endregion

  get shouldShowManageGroupOrderBanner(): boolean {
    const order = this.$services.order.getCurrentOrder();
    return !!order && order.order.orderType == OrderType.Group && this.$services.order.isCustomerHostOfGroupOrder();
  }

  get isPointsCelebrationVisible(): boolean {
    return this.$appStore.state.ui.pointsCelebrationVisible;
  }

  get shouldScrollToHash(): boolean {
    return this.$appStore.state.ui.home.shouldScrollToHash;
  }

  get isRewardsExchangeOnline(): boolean {
    return this.$appStore.state.featureFlags.orderingStatus?.isRewardsExchangeOnline ?? true;
  }

  get isRewardsOnline(): boolean {
    return this.$appStore.state.featureFlags.orderingStatus?.isRewardsOnline ?? true;
  }

  get isLoyaltyEnabled(): boolean {
    return isLoyaltyEnabled(this.$appStore.state.ui.countryProfile.countryCode);
  }

  // Display one-tap order section between rewards banner and top-level menu
  get isFeaturedSectionAboveTopLevelMenu(): boolean {
    return this.$appStore.state.adobeTarget.isFeaturedSectionAboveTopLevelMenu;
  }

  async showAEMExperienceFragments() {
    if( !!document.getElementById('aem-xf-hero') ) {
      // If navigating back to AEM Homepage, show homepage experience fragments.
      document.getElementById('aem-xf-hero')?.classList.remove('d-none');
      document.getElementById('aem-homepage-main')?.classList.remove('d-none');
      document.getElementById('aem-xf-tout')?.classList.remove('d-none'); // TODO: Placed under #aem-homepage-main, consider removing
      document.getElementById('aem-featured-meals')?.classList.remove('d-none'); // TODO: Placed under #aem-homepage-main, consider removing
      document.getElementById('aem-xf-crowd-pleasers')?.classList.remove('d-none'); // TODO: Placed under #aem-homepage-main, consider removing
    } else if( !this.isLoadingAemXfs ) {
      console.log("AemHome - hero isn't here, requesting all the homepage XF's HTML files");
      this.isLoadingAemXfs = true;
      for( let xf of AEMHomepageXfs ) {
        let url = this.countryCode === CountryCode.US ? xf.url : xf.url.replace('/us/', '/' + this.countryCode.toLowerCase() + '/');
        let html = await getAemXfHtml(url, xf.rootSelector);
        if( html ) {
          let parent = document.createElement('div');
          parent.id = xf.id;
          parent.appendChild(html as Node);
          xf.cb(parent);
        }
      }
      this.isLoadingAemXfs = false;
    }
  }

  hideAEMExperienceFragments() {
    // If navigating away from AEM Homepage, hide homepage experience fragments.
    document.getElementById('aem-xf-hero')?.classList.add('d-none');
    document.getElementById('aem-homepage-main')?.classList.add('d-none');
    document.getElementById('aem-xf-tout')?.classList.add('d-none'); // TODO: Placed under #aem-homepage-main, consider removing
    document.getElementById('aem-featured-meals')?.classList.add('d-none'); // TODO: Placed under #aem-homepage-main, consider removing
    document.getElementById('aem-xf-crowd-pleasers')?.classList.add('d-none'); // TODO: Placed under #aem-homepage-main, consider removing
  }

  addClickTrackingToAEMExperienceFragments() {
    console.log("Adding Adobe tracking to Homepage XFs CTAs");
    let ctas = document.querySelectorAll('.cmp-button, a.aem-hero__button, a.aem-card__button') as NodeListOf<HTMLAnchorElement>;
    let hasDefinedFields = false;
    let isTrackedAlready = false;
    for( let cta of ctas) {
      hasDefinedFields = !!(cta && cta.dataset && (cta.dataset.linkSection || cta.dataset.linkPosition || cta.dataset.linkCategory || cta.dataset.linkTitle));
      isTrackedAlready = (!cta.dataset.isTracking || cta.dataset.isTracking === 'false');
      if( hasDefinedFields && isTrackedAlready) {
        cta.dataset.isTracking = 'true';
        cta.addEventListener('click', (e) => {
          console.log('clicked for analytics', this.$services);
          this.$services.analytics.addLinkClickedEventToStore({
            linkClicks: {value: 1},
            linkSection: cta.dataset?.linkSection,
            linkPosition: cta.dataset?.linkPosition,
            linkCategory: cta.dataset?.linkCategory,
            linkName: cta.text?.trim() || '',
            linkTitle: cta.dataset?.linkTitle
          }, cta.href || "");
        });
      }
    }
  }

  async beforeMount() {
    // Determine if the hero is missing from page source code and needs to be dynamically requested
    this.isAemHeroXFMissing = !document.querySelector('#en-home-page > .experiencefragment > #aem-xf-hero');
  }

  async mounted() {
    this.addClickTrackingToAEMExperienceFragments();
    // If navigating back to AEM Homepage, show homepage experience fragments again.
    this.showAEMExperienceFragments();

    this.watchers.push(this.$appStore.watch(state => state.menu.activeMenu, this.scrollToHash, { immediate: true }));
    this.watchers.push(this.$appStore.watch(state => state.restaurant.selectedRestaurant, this.onRestaurantSelected, { immediate: true }));

    // Check if online ordering flag was turned off in APIM
    await this.$services.featureFlags.validateOnlineOrdering();

    const query = this.$router.currentRoute.query;
    // If any utm params are provided, create external marketing campaign event
    if (query?.utm_campaign || query?.utm_content || query?.utm_content || query?.utm_medium || query?.utm_source || query?.utm_term) {
      this.$services.analytics.send(new ExternalCampaignEvent(query));
    } else {
      this.$services.analytics.send(new HomePageLoadEvent());
    }
    
    this.$services.adobeTarget.sendAdobeTargetExperienceRequest(this.$route.name);

  }

  updated() {
    this.showAEMExperienceFragments();
  }

  destroyed() {
    console.log('destroyed CmgAemHome');
    this.hideAEMExperienceFragments();
    for (const unsubFn of this.watchers) {
      unsubFn();
    }
  }

  // This is so components like the bag can issue a command to scroll when page is already at the correct route
  @Watch('shouldScrollToHash')
  onShouldScrollToHash(shouldScroll: boolean) {
    if (shouldScroll) {
      this.hasScrolledToHash = false;
      this.scrollToHash();
      this.$services.ui.setShouldScrollHomeToHash(false);
    }
  }

  private scrollToHash() {
    if (!this.$route.hash || this.hasScrolledToHash) {
      return;
    }
    const routeHash: string = this.$route.hash.replace('#', '');
    const componentEl = document.getElementById(routeHash);
    if (componentEl) {
      this.hasScrolledToHash = true;
      this.$nextTick(() => componentEl.scrollIntoView());
    }
  }

  private onRestaurantSelected() {
    this.$services.logger.debug('Refreshing data after restaurant selection...');

    this.refreshFavoriteOrders();
    this.refreshPreConfiguredMeals();
  }

  private async refreshFavoriteOrders(): Promise<any> {
    if (this.isLoadingFavorites) {
      return;
    }
    this.isLoadingFavorites = true;

    try {
      await this.$services.recentAndFavoriteOrders.refreshFavoriteOrders();
    } catch (e) {
      this.$services.logger.error('Something went wrong while refreshing favorite orders', undefined, e);
    } finally {
      this.isLoadingFavorites = false;
    }
  }

  private async refreshPreConfiguredMeals() {
    const restaurant = this.$appStore.state.restaurant.selectedRestaurant;

    try {
      if (!restaurant) {
        await this.$services.menu.refreshPreConfiguredMeals().then(() => this.scrollToHash()); // Universal PCEs
        return;
      } else {
        await this.$services.menu.refreshPreConfiguredMeals(restaurant.restaurantNumber, true).then(() => this.scrollToHash());
      }
    } catch (e) {
      this.$services.logger.error('Something went wrong while fetching PreConfiguredMeal data: ', undefined, e);
    }
  }
}
